// components/SustainabilitySection.tsx
"use client";

import React, { useState } from "react";
import Image from "next/image";
import { sustainabilitySections } from "./data/sustainabilityData";

interface SustainabilityItemProps {
  section: (typeof sustainabilitySections)[number];
  index: number;
}

const SustainabilityItem: React.FC<SustainabilityItemProps> = ({ section, index }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [secondImageLoaded, setSecondImageLoaded] = useState(false);

  // Layout pattern: 0 = left side, 1 = full width, 2 = right side, 3 = both sides with middle content
  const layoutType = index % 4;
  const formattedContent = section.content.split("\n").map((paragraph, idx) => (
    <p key={idx} className="text-gray-700 text-xs leading-relaxed mb-2">
      {paragraph}
    </p>
  ));

  // Get the image URL - use imageUrls[0] if available, otherwise fall back to imageUrl
 const primaryImageUrl =
  section.imageUrls?.[0] || "/images/placeholder.jpg";


  // For Layout 3, check if we have multiple images
  const hasMultipleImages = section.imageUrls && section.imageUrls.length > 1;
  const leftImageUrl = hasMultipleImages ? section.imageUrls[0] : primaryImageUrl;
  const rightImageUrl = hasMultipleImages ? section.imageUrls[1] : primaryImageUrl;

  return (
    <section key={section.id} className="py-8 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Layout 0 → Left side image */}
        {layoutType === 0 && (
          <div className="grid lg:grid-cols-2 gap-6 ">
            {/* Image on Left */}
            <div className="relative overflow-hidden duration-300">
              <div className="relative aspect-[4/3]">
                <Image
                  src={primaryImageUrl}
                  alt={section.title}
                  fill
                  className={`object-cover transition-opacity duration-500 ${
                    imageLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority={index === 0}
                />
                {!imageLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
              </div>
            </div>

            {/* Content on Right */}
            <div className="lg:pl-6">
              {section.version && (
                <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded mb-2">
                  {section.version}
                </span>
              )}
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
              <div className="text-gray-700 leading-relaxed">
                {formattedContent}
              </div>

              {section.bullets && section.bullets.length > 0 && (
                <ul className="space-y-2 mt-3">
                  {section.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start text-xs text-gray-700">
                      <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">•</span>
                      <span dangerouslySetInnerHTML={{ __html: bullet }} />
                    </li>
                  ))}
                </ul>
              )}

              {section.badges && section.badges.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {section.badges.map((badge, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded border border-gray-200"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Layout 1 → Full width */}
        {layoutType === 1 && (
         <div className="grid lg:grid-cols-2 gap-6 items-center">
    {/* Content on Left */}
    <div className="lg:pr-6">
      {section.version && (
        <span className="inline-block px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded mb-2">
          {section.version}
        </span>
      )}
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
      <div className="text-gray-700 leading-relaxed">
        {formattedContent}
      </div>

      {section.bullets && section.bullets.length > 0 && (
        <ul className="space-y-2 mt-3">
          {section.bullets.map((bullet, idx) => (
            <li key={idx} className="flex items-start text-xs text-gray-700">
              <span className="text-purple-500 mr-2 mt-0.5 flex-shrink-0">•</span>
              <span dangerouslySetInnerHTML={{ __html: bullet }} />
            </li>
          ))}
        </ul>
      )}

      {section.badges && section.badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {section.badges.map((badge, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded border border-gray-200"
            >
              {badge}
            </span>
          ))}
        </div>
      )}
    </div>

    {/* Image on Right - Smaller and centered */}
    <div className="flex justify-center">
      <div className="relative overflow-hidden  transition-shadow duration-300 max-w-md w-full">
        <div className="relative aspect-[4/3]"> {/* Changed from aspect-[1/1] to [4/3] for shorter height */}
          <Image
            src={primaryImageUrl}
            alt={section.title}
            fill
            className={`object-cover transition-opacity duration-500 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImageLoaded(true)}
            sizes="(max-width: 1024px) 100vw, 40vw"
          />
          {!imageLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
        </div>
      </div>
    </div>
  </div>
        )}

      {/* Layout 3 → Right side image */}
{layoutType === 3 && (
  <div className="grid lg:grid-cols-2 gap-6 items-center">
    {/* Content on Left */}
    <div className="lg:pr-6">
      {section.version && (
        <span className="inline-block px-2 py-0.5 bg-purple-50 text-purple-700 text-xs font-medium rounded mb-2">
          {section.version}
        </span>
      )}
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
      <div className="text-gray-700 leading-relaxed">
        {formattedContent}
      </div>

      {section.bullets && section.bullets.length > 0 && (
        <ul className="space-y-2 mt-3">
          {section.bullets.map((bullet, idx) => (
            <li key={idx} className="flex items-start text-xs text-gray-700">
              <span className="text-purple-500 mr-2 mt-0.5 flex-shrink-0">•</span>
              <span dangerouslySetInnerHTML={{ __html: bullet }} />
            </li>
          ))}
        </ul>
      )}

      {section.badges && section.badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {section.badges.map((badge, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded border border-gray-200"
            >
              {badge}
            </span>
          ))}
        </div>
      )}
    </div>

    {/* Image on Right - Smaller and centered */}
    <div className="flex justify-center">
      <div className="relative overflow-hidden  transition-shadow duration-300 max-w-md w-full">
        <div className="relative aspect-[4/3]"> {/* Changed from aspect-[1/1] to [4/3] for shorter height */}
          <Image
            src={primaryImageUrl}
            alt={section.title}
            fill
            className={`object-cover transition-opacity duration-500 ${
              imageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setImageLoaded(true)}
            sizes="(max-width: 1024px) 100vw, 40vw"
          />
          {!imageLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
        </div>
      </div>
    </div>
  </div>
)}

        {/* Layout 2 → Both side images with middle content - DYNAMIC IMAGES */}
        {layoutType === 2 && (
          <div className="grid lg:grid-cols-3 gap-6 items-center">
            {/* Left Image - Dynamic */}
            <div className="relative overflow-hidden rounded  transition-shadow duration-300">
              <div className="relative aspect-square">
                <Image
                  src={leftImageUrl}
                  alt={hasMultipleImages ? `${section.title} - Image 1` : `${section.title} - Left`}
                  fill
                  className={`object-cover transition-opacity duration-500 ${
                    imageLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  sizes="(max-width: 1024px) 100vw, 33vw"
                />
                {!imageLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
              </div>
            </div>

            {/* Middle Content */}
            <div className="text-center">
              {section.version && (
                <span className="inline-block px-2 py-0.5 bg-orange-50 text-orange-700 text-xs font-medium rounded mb-2">
                  {section.version}
                </span>
              )}
              <h2 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h2>
              <div className="text-gray-700 leading-relaxed">
                {formattedContent}
              </div>

              {section.bullets && section.bullets.length > 0 && (
                <ul className="space-y-2 mt-3">
                  {section.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start text-xs text-gray-700 text-left">
                      <span className="text-orange-500 mr-2 mt-0.5 flex-shrink-0">•</span>
                      <span dangerouslySetInnerHTML={{ __html: bullet }} />
                    </li>
                  ))}
                </ul>
              )}

              {section.badges && section.badges.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4 justify-center">
                  {section.badges.map((badge, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded border border-gray-200"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Right Image - Dynamic */}
            <div className="relative overflow-hidden rounded  transition-shadow duration-300">
              <div className="relative aspect-square">
                <Image
                  src={rightImageUrl}
                  alt={hasMultipleImages ? `${section.title} - Image 2` : `${section.title} - Right`}
                  fill
                  className={`object-cover transition-opacity duration-500 ${
                    secondImageLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  onLoad={() => setSecondImageLoaded(true)}
                  sizes="(max-width: 1024px) 100vw, 33vw"
                />
                {!secondImageLoaded && <div className="absolute inset-0 bg-gray-200 animate-pulse" />}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const SustainabilitySection: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Main Title */}
      <div className="text-center pt-20 pb-4 max-w-5xl mx-auto">
        <h1 className="text-center text-2xl font-light mb-2">Sustainability</h1>
        <p className="text-xs text-gray-600">
          Our commitment towards a greener future
        </p>
      </div>

      {/* All Sections */}
      {sustainabilitySections.map((section, index) => (
        <SustainabilityItem key={section.id} section={section} index={index} />
      ))}
    </div>
  );
};

export default SustainabilitySection;